import { voiceTrim, secondsToTime } from "./soundTrim";

// Trimming a character's voice (Text to Speech or recorded voice) by dragging the edges of its
// voice block on the timeline. Same fields as the character's Voice settings (VoiceTrimSettings):
//   TTSTrimStart / TTSTrimEnd   seconds of the voice file that play
//   midTransitionWidths         length of the voice block on the timeline (px)
//   LastStartPositionOnTimeline where the voice starts on the timeline (px; editor, preview, export)
//   StartAudioTime              the same, used by the editor's audio sync and the video export
//   div_TTS_Left                the block's offset inside the character's bar
// Left edge: the voice starts later in its file and later on the timeline by the same amount, so
// the words that remain stay where they were. Right edge: only the end changes.

const MIN_SECONDS = 0.2;

/** Where the voice block starts on the timeline (px). */
export function voiceStartPx(layer) {
  const last = Number(layer.LastStartPositionOnTimeline);
  return Number.isFinite(last) ? last : Number(layer.startPositionOnTimeline) || 0;
}

/**
 * The layer's fields after dragging the voice block's `edge` ("left" | "right") by `deltaPx`
 * (positive = to the right). `onePxMs` is milliseconds per timeline pixel. Returns null when the
 * voice length is unknown.
 */
export function trimVoiceBar(layer, edge, deltaPx, onePxMs) {
  if (!layer || !layer.TTSName || !(onePxMs > 0)) return null;
  const t = voiceTrim(layer);
  const full = t.full;
  if (!(full > 0)) return null;
  const pxPerSec = 1000 / onePxMs;
  const barStart = Number(layer.startPositionOnTimeline) || 0;
  const barEnd = barStart + (Number(layer.widthOnTimeline) || 0);
  let startPx = voiceStartPx(layer);
  let trimStart = t.start;
  let trimEnd = t.end;

  if (edge === "left") {
    let dSec = deltaPx / pxPerSec;
    // Not before the start of the file, not before the character appears, keep a little voice.
    dSec = Math.max(dSec, -trimStart, (barStart - startPx) / pxPerSec);
    dSec = Math.min(dSec, trimEnd - MIN_SECONDS - trimStart);
    trimStart += dSec;
    startPx += dSec * pxPerSec;
  } else {
    let dSec = deltaPx / pxPerSec;
    const roomSec = barEnd > startPx ? (barEnd - startPx) / pxPerSec : Infinity;
    trimEnd = Math.min(full, trimEnd + dSec, trimStart + roomSec);
    trimEnd = Math.max(trimEnd, trimStart + MIN_SECONDS);
  }

  trimStart = Math.round(trimStart * 100) / 100;
  trimEnd = Math.round(trimEnd * 100) / 100;
  startPx = Math.round(startPx);
  const lengthPx = Math.max(1, Math.round((trimEnd - trimStart) * pxPerSec));
  const isFull = trimStart <= 0.01 && trimEnd >= full - 0.01;

  const next = {
    midTransitionWidths: lengthPx,
    TTSWidthOnTimeline: lengthPx,
    completeWithOfTTS: lengthPx,
    LastStartPositionOnTimeline: startPx,
    StartAudioTime: startPx,
    StartTimeOfTTS: secondsToTime((startPx * onePxMs) / 1000),
    div_TTS_Left: Math.max(0, startPx - barStart) + "px",
    TTSDuration: layer.TTSDuration || secondsToTime(full),
  };
  if (isFull) {
    next.TTSTrimStart = undefined;
    next.TTSTrimEnd = undefined;
  } else {
    next.TTSTrimStart = trimStart;
    next.TTSTrimEnd = trimEnd;
  }
  return next;
}

/** "1.2 s – 4.8 s of 9.0 s" for the drag tooltip. */
export function voiceTrimLabel(layerWithTrim) {
  const t = voiceTrim(layerWithTrim);
  return `${t.start.toFixed(1)} s – ${t.end.toFixed(1)} s of ${t.full.toFixed(1)} s`;
}
